// Camera QR scanning. Prefers the native BarcodeDetector API (Chrome/Edge/
// Android, recent Safari); when it's missing — desktop Chrome on macOS/Windows,
// Firefox, many setups — it transparently falls back to the jsQR decoder over a
// canvas snapshot of the video, so scanning works on essentially any device
// with a camera. `state === 'unsupported'` is now reached only when the browser
// exposes no camera (getUserMedia) at all.

import jsQR from 'jsqr';
import { useEffect, useRef, useState } from 'react';

// Minimal typings for the BarcodeDetector API (not yet in lib.dom).
type DetectedBarcode = { rawValue: string; format: string };
type BarcodeDetectorInstance = {
  detect: (source: CanvasImageSource) => Promise<DetectedBarcode[]>;
};
type BarcodeDetectorCtor = {
  new (opts?: { formats?: string[] }): BarcodeDetectorInstance;
  getSupportedFormats?: () => Promise<string[]>;
};

function getCtor(): BarcodeDetectorCtor | null {
  return (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector ?? null;
}

export type ScannerState = 'idle' | 'starting' | 'scanning' | 'unsupported' | 'denied' | 'error';

type Options = {
  active: boolean;
  onResult: (value: string) => void;
};

// jsQR fallback tuning: decode at most ~11×/s on a frame downscaled so its
// longest side is ≤ this many px — plenty for a QR yet cheap enough for a phone.
const FALLBACK_SCAN_INTERVAL_MS = 90;
const FALLBACK_MAX_DIM = 720;

export function useQrScanner({ active, onResult }: Options) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [state, setState] = useState<ScannerState>('idle');
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    if (!active) {
      setState('idle');
      return;
    }
    // getUserMedia is mandatory; BarcodeDetector is optional (jsQR fills in).
    if (!navigator.mediaDevices?.getUserMedia) {
      setState('unsupported');
      return;
    }

    const Ctor = getCtor();
    const detector = Ctor ? new Ctor({ formats: ['qr_code'] }) : null;

    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;
    let fired = false;
    let lastScan = 0;
    // Offscreen canvas reused across frames for the jsQR fallback path only.
    let canvas: HTMLCanvasElement | null = null;
    let ctx: CanvasRenderingContext2D | null = null;

    setState('starting');

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then(async (s) => {
        if (stopped) {
          for (const t of s.getTracks()) t.stop();
          return;
        }
        stream = s;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = s;
        video.setAttribute('playsinline', 'true');
        await video.play().catch(() => {});
        setState('scanning');
        tick();
      })
      .catch((err: unknown) => {
        if (stopped) return;
        const name = (err as { name?: string }).name;
        setState(name === 'NotAllowedError' ? 'denied' : 'error');
      });

    function emit(value: string) {
      if (fired) return;
      fired = true;
      onResultRef.current(value);
    }

    function scanWithJsQr(video: HTMLVideoElement) {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) return;
      const scale = Math.min(1, FALLBACK_MAX_DIM / Math.max(vw, vh));
      const w = Math.round(vw * scale);
      const h = Math.round(vh * scale);
      if (!canvas) {
        canvas = document.createElement('canvas');
        ctx = canvas.getContext('2d', { willReadFrequently: true });
      }
      if (!ctx || !canvas) return;
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(video, 0, 0, w, h);
      const img = ctx.getImageData(0, 0, w, h);
      const code = jsQR(img.data, w, h, { inversionAttempts: 'attemptBoth' });
      if (code?.data) emit(code.data);
    }

    async function tick() {
      if (stopped) return;
      const video = videoRef.current;
      if (video && video.readyState >= 2) {
        try {
          if (detector) {
            const codes = await detector.detect(video);
            const hit = codes.find((c) => c.rawValue);
            if (hit) {
              emit(hit.rawValue);
              return; // stop loop after a successful read
            }
          } else {
            const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
            if (now - lastScan >= FALLBACK_SCAN_INTERVAL_MS) {
              lastScan = now;
              scanWithJsQr(video);
              if (fired) return;
            }
          }
        } catch {
          // transient decode error — keep scanning
        }
      }
      raf = requestAnimationFrame(tick);
    }

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      if (stream) for (const t of stream.getTracks()) t.stop();
    };
  }, [active]);

  return { videoRef, state };
}
